import React from 'react';
import get from 'lodash/get';

import { Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Criterium, CriteriumSkeleton } from '@hydra/common/criterium';
import { Label, Translate } from '@hydra/common/components';
import ReviewCriteriaCTA from '@hydra/common/components/CTA/ReviewCriteriaCTA';
import ReviewSkeleton from '@hydra/common/components/ReviewSkeleton';
import { useReviewContext } from '@hydra/common/context';
import { CriteriumInterface } from '@hydra/types/review';

import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.up('pdfPortrait')]: {
      display: 'flex'
    },
    '& hr': {
      margin: theme.spacing(0, 2.5),
      alignSelf: 'stretch',
      height: 'auto',
      width: 1
    }
  },
  side: {
    [theme.breakpoints.up('pdfPortrait')]: {
      width: '50%'
    }
  },
  uptimeData: {
    paddingBottom: theme.spacing(1.5),
    fontWeight: theme.typography.fontWeightSemibold,
    fontSize: 22
  },
  infoNumber: {
    display: 'block',
    color: theme.palette.grey[500],
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold
  }
}));

interface PjUptimeCriterium extends CriteriumInterface {
  data: {
    check: {
      upid: number
      lastCheckDate: number
      lastCheckStatus: string
      lastCheckReponse: string
    }
    stats: {
      uptimePercentage: number
      lowestResponseTime: number
      averageResponseTime: number
      numberOfChecks: number
      startDate: number
      endDate: number
    },
    details: {
      uptimePercentage: number
      startDate: number
      endDate: number
    }[]
  }
}

export const PjUptime = ({
  criterium,
  url
}: {
  criterium: PjUptimeCriterium
  url: string
}) => {
  const { moment, numeral } = useI18n();
  const classes = useStyles();
  const { isFree } = useReviewContext();
  const { variant } = useReviewContext();

  if (isFree) {
    return (
      <Criterium criterium={{ type: 'pj_uptime', score: null, importance: 0, solvability: 0, status: 'neutral', url, updatedAt: (new Date()).getTime() }} clickable={false} align='center'>
        <ReviewCriteriaCTA
          text={<Translate component='div' content='pj_uptime_cta_text' with={{ domain: url }} />}
          event='serveruptime'
        />
      </Criterium>
    );
  }

  if (!criterium) {
    return (
      <Criterium criterium={{ type: 'pj_uptime', score: null, importance: 0, solvability: 0, status: 'neutral', url, updatedAt: (new Date()).getTime() }} clickable={false}>
        <ReviewSkeleton height={137} />
      </Criterium>
    );
  }

  const hasCheck = get(criterium, ['data', 'check'], false);
  const partialDataStatus = criterium.data?.check?.lastCheckStatus === 'OK' ? 'success' : 'error';
  const partialDataText = criterium.data?.check?.lastCheckStatus === 'OK' ? 'uptime_check_pass' : 'uptime_check_fail';

  const hasStats = get(criterium, ['data', 'stats'], false);
  const uptimeDays = moment(criterium.data?.stats?.endDate).diff(criterium.data?.stats?.startDate, 'days', true);

  const content = (
    <>
      {
        !hasCheck || !criterium.data?.check?.lastCheckDate
          ? (variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : <Translate content='uptime_no_data' />)
          : null
      }
      {
        hasCheck && !hasStats
          ? (
            <>
              {variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : <Translate content={partialDataText} />}
              <Label inline style={partialDataStatus}>{variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : criterium.data?.check?.lastCheckReponse}</Label>
            </>
            )
          : null
      }
      {
        hasStats
          ? (
            <div className={classes.root}>
              <div className={classes.side}>
                {variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : <Translate content='uptime_days_results' with={{ uptimeDays: Math.round(uptimeDays) }} />}
                <div className={classes.uptimeData}>
                  <span>{variant === 'nocrit' ? <CriteriumSkeleton width='20%' /> : numeral(criterium.data?.stats?.uptimePercentage).format('0.[00]')}</span>%
                  <div className={classes.infoNumber}>
                    <Translate component='span' content='uptime_total_checks' />{variant === 'nocrit' ? <CriteriumSkeleton width='20%' /> : numeral(criterium.data?.stats?.numberOfChecks).format('0,0')}
                  </div>
                </div>
              </div>
              <Divider />
              <div className={classes.side}>
                <Translate content='uptime_average_response_time' />
                <div className={classes.uptimeData}>
                  <span>{variant === 'nocrit' ? <CriteriumSkeleton width='20%' /> : numeral(criterium.data?.stats?.averageResponseTime).format('0,0')}</span>ms
                  <div className={classes.infoNumber}>
                    <Translate content='uptime_lowest_response_time' />{`: ${variant === 'nocrit' ? <CriteriumSkeleton width='20%' /> : numeral(criterium.data?.stats?.lowestResponseTime).format('0,0')} ms`}
                  </div>
                </div>
              </div>
            </div>
            )
          : null
      }
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

export default React.memo(PjUptime);
